import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useLocation } from 'react-router';

import { useUserLocation } from 'Account/hooks';
import { EventType } from 'Book/shared/types';
import {
  talkEventRequestsQuery_unmindEventRequests_edges_node as EventRequest,
  talkEventRequestsQuery,
} from 'Services/BeGateway/Talk/__generated__/talkEventRequestsQuery';
import {
  talkEventsQuery_unmindEvents_edges_node as Event,
  talkEventsQuery,
} from 'Services/BeGateway/Talk/__generated__/talkEventsQuery';
import {
  TALK_EVENTS_QUERY,
  TALK_EVENT_REQUESTS_QUERY,
} from 'Services/BeGateway/Talk/talk.services';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';
import { useCoachingAndTherapyUserDetails } from '../useCoachingAndTherapyUserDetails';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDemoData = (isDemo: boolean, state: any) => {
  const demoEvent: Event | null = isDemo && !!state?.event ? state.event : null;

  const demoEventRequest: EventRequest | null =
    isDemo && !!state?.eventRequest ? state.eventRequest : null;

  return { demoEvent, demoEventRequest };
};

export default ({
  skip = false,
}: {
  /**
   * Skips the Talk queries entirely, for callers mounted where the user may
   * not have the Talk entitlement. The hook still returns its usual shape, so
   * callers can read it unconditionally: no appointments, not loading, no
   * error.
   */
  skip?: boolean;
} = {}) => {
  const { state } = useLocation();
  const [staticDate] = useState(new Date().toISOString());

  const { userLocationData } = useUserLocation();
  const userID = userLocationData?.user?.id;
  const { user } = useCoachingAndTherapyUserDetails();

  const eventRequestVariables = {
    first: 10,
    after: null,
    unmindUserId: userID ?? '',
    ordering: [
      {
        sort: 'BEGINS_AT',
        direction: 'ASC',
      },
    ],
    filter: {
      where: {
        endsAt: {
          moreThan: staticDate,
        },
      },
    },
  };
  const {
    data: events,
    loading: eventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery<talkEventsQuery>(TALK_EVENTS_QUERY, {
    variables: {
      ...eventRequestVariables,
    },
    skip: skip || !userID,
    ...defaultQueryConfig,
    ...BEGatewayQueryContext,
  });

  const eventRequestQueryVariables = {
    first: 10,
    after: null,
    unmindUserId: userID ?? '',
  };
  const {
    data: eventRequests,
    loading: eventRequestsLoading,
    error: eventRequestError,
    refetch: refetchEventRequests,
  } = useQuery<talkEventRequestsQuery>(TALK_EVENT_REQUESTS_QUERY, {
    variables: { ...eventRequestQueryVariables },
    skip: skip || !userID,
    ...defaultQueryConfig,
    ...BEGatewayQueryContext,
  });

  const {
    demoEvent,
    demoEventRequest,
  }: {
    demoEvent: Event | null;
    demoEventRequest: EventRequest | null;
    // Gated on `skip` too, so a skipped hook reports no appointments of any
    // kind rather than smuggling a demo booking through.
  } = getDemoData(!skip && !!user.isDemo, state);

  return {
    loading: eventsLoading || eventRequestsLoading,
    error: (eventRequestError && !eventRequests) || (eventsError && !events),
    // The raw Apollo error behind `error`, for callers that need to log the
    // failure rather than just branch on it.
    apolloError: eventsError ?? eventRequestError,
    refetch: () => {
      // Mirrors the `skip` on both queries above. Apollo's `skip` only gates
      // the automatic fetch: `refetch` overrides the resulting `standby`
      // fetch policy with `network-only`, so without this guard a caller's
      // retry would fire Talk queries for a user with no Talk entitlement, or
      // fire them with an empty `unmindUserId`.
      if (skip || !userID) {
        return;
      }

      void refetchEventRequests(eventRequestQueryVariables);
      void refetchEvents(eventRequestVariables);
    },

    data: {
      bookingType:
        !!events?.unmindEvents?.edges?.length || !!demoEvent
          ? EventType.EVENT
          : EventType.EVENT_REQUEST,
      numberOfEvents:
        events?.unmindEvents.count ?? events?.unmindEvents?.edges?.length ?? 0,
      upcomingEvents: !!demoEvent
        ? [demoEvent]
        : (events?.unmindEvents.edges
            ?.map(edge => edge?.node)
            .filter((node): node is Event => !!node) ?? null),
      upcomingEventRequests: !!demoEventRequest
        ? [demoEventRequest]
        : (eventRequests?.unmindEventRequests.edges
            ?.map(edge => edge?.node)
            .filter((node): node is EventRequest => !!node) ?? null),
      userID,
    },

    flags: {
      hasDemoBooking: !!demoEvent || !!demoEventRequest,
      hasBooking: !!events?.unmindEvents?.edges?.length,
      hasRequest: !!eventRequests?.unmindEventRequests?.edges?.length,
    },
  };
};
